import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Loader2, Lock } from 'lucide-react'
import FilterBar from '../components/ui/FilterBar'
import LogoItem from '../components/ui/LogoItem'
import { useAuth } from '../context/AuthContext'
import AuthModal from '../components/ui/AuthModal'

export default function AllLogosPage() {
  const { isAuthenticated, loading: authLoading } = useAuth()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [logos, setLogos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filters, setFilters] = useState({
    search: '',
  })

  // Fetch logos from MongoDB via API
  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated) {
        setShowAuthModal(true)
        setLoading(false)
      } else {
        fetchLogos()
      }
    }
  }, [isAuthenticated, authLoading])

  const fetchLogos = async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      if (filters.search) params.append('search', filters.search)

      const response = await fetch(`/api/cloudinary/logos?${params}`)
      if (!response.ok) {
        throw new Error('Failed to fetch logos')
      }

      const data = await response.json()
      setLogos(data.logos || [])
    } catch (err) {
      console.error('Error fetching logos:', err)
      setError('Failed to load logos. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Re-fetch when search filter changes (only if authenticated)
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      const debounce = setTimeout(() => {
        fetchLogos()
      }, 300)
      return () => clearTimeout(debounce)
    }
  }, [filters.search, isAuthenticated, authLoading])

  const filteredLogos = logos.filter((logo) => {
    if (!filters.search) return true
    const searchLower = filters.search.toLowerCase()
    return (
      logo.name.toLowerCase().includes(searchLower) ||
      logo.description?.toLowerCase().includes(searchLower) ||
      logo.category?.toLowerCase().includes(searchLower)
    )
  })

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-nitrr-background dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-nitrr-primary mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    )
  }

  // Show auth required if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => window.location.href = '/'}
          defaultMode="signin"
        />
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-blue-900 dark:to-purple-900 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 text-center"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-6 shadow-lg">
              <Lock className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Authentication Required
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Please sign in to view all logos and access premium downloads.
            </p>
            <button
              onClick={() => setShowAuthModal(true)}
              className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Sign In to Continue
            </button>
          </motion.div>
        </div>
      </>
    )
  }

  return (
    <div className="min-h-screen bg-nitrr-background dark:bg-gray-950">
      <div className="container mx-auto px-4 py-12 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl md:text-4xl font-bold text-nitrr-text dark:text-white mb-2">
            All Logos
          </h1>
          <p className="text-nitrr-text-secondary dark:text-gray-300">
            {loading ? 'Loading...' : `${filteredLogos.length} logos available - Transform and download in any format`}
          </p>
        </motion.div>

        <FilterBar filters={filters} setFilters={setFilters} />

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-12 h-12 animate-spin text-nitrr-primary" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button
              onClick={fetchLogos}
              className="px-6 py-2 bg-nitrr-primary text-white rounded-lg hover:bg-nitrr-primary-dark transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : filteredLogos.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-nitrr-text-secondary dark:text-gray-400 mb-2">
              {filters.search
                ? `No logos found matching "${filters.search}". Try a different search term.`
                : 'No logos available yet. Contact admin to upload logos.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredLogos.map((logo, index) => (
              <LogoItem key={logo._id || logo.id} logo={logo} index={index} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
